import { useState } from "react"

import { useMsal } from "@azure/msal-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { getToken } from "@/api/auth-api"
import { approveMemoryCandidateApi, dismissMemoryCandidateApi, getMemoriesApi } from "@/api/memory"
import { Check, ChevronDown, ChevronUp, Lightbulb, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { handleDismissMemoryCandidateSuccess } from "./memory-candidate-utils"

type MemoryCandidateProps = {
  text: string
}

export function MemoryCandidate({ text }: MemoryCandidateProps) {
  const { t } = useTranslation()
  const { instance } = useMsal()
  const queryClient = useQueryClient()
  const [isHidden, setIsHidden] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const isTruncated = text.length > 60

  const { data: memories } = useQuery({
    queryKey: ["memories"],
    queryFn: async () => {
      const tokenResult = await getToken(instance)
      return getMemoriesApi(tokenResult?.accessToken)
    },
  })

  const alreadyApproved =
    memories?.stm.some((item) => item.text === text && item.status === "active") ||
    memories?.ltm.some((item) => item.text === text)
  const alreadyDismissed = memories?.discarded_candidate_texts?.includes(text) ?? false

  const approveMutation = useMutation({
    mutationFn: async () => {
      const tokenResult = await getToken(instance)
      return approveMemoryCandidateApi(text, tokenResult?.accessToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["memories"] })
      setIsHidden(true)
      toast.success(t("personalization.memoryPanel.toasts.saved"))
    },
    onError: () => {
      toast.error(t("personalization.memoryPanel.toasts.saveFailed"))
    },
  })

  const dismissMutation = useMutation({
    mutationFn: async () => {
      const tokenResult = await getToken(instance)
      return dismissMemoryCandidateApi(text, tokenResult?.accessToken)
    },
    onSuccess: async () => {
      await handleDismissMemoryCandidateSuccess({
        queryClient,
        setHidden: setIsHidden,
        // memory-candidate-utils.ts expects the shadcn-style `(options) => void`
        // shape; adapt sonner's toast() to it here rather than changing that
        // utility's signature.
        toast: ({ title }) => toast.success(title),
        toastTitle: t("personalization.memoryPanel.toasts.dismissed"),
      })
    },
    onError: () => {
      toast.error(t("personalization.memoryPanel.toasts.dismissFailed"))
    },
  })

  if (isHidden) {
    return null
  }

  if (alreadyApproved || alreadyDismissed) {
    return null
  }

  const handleBadgeClick = () => {
    if (isTruncated) {
      setIsExpanded(!isExpanded)
    }
  }

  return (
    <Badge
      role="presentation"
      variant="outline"
      className={cn(
        "border-primary/30 bg-primary/5 hover:bg-primary/10 my-1 inline-flex items-center gap-2 px-3 py-1.5 text-sm transition-colors",
        isExpanded && "max-w-full flex-wrap",
        isTruncated && "cursor-pointer"
      )}
      onClick={handleBadgeClick}
    >
      <Lightbulb className="text-primary h-3.5 w-3.5 shrink-0" />
      <span
        title={!isExpanded ? text : undefined}
        className={cn(
          "text-xs",
          !isExpanded && isTruncated && "max-w-[300px] truncate",
          isExpanded && "break-words whitespace-normal"
        )}
      >
        {text}
      </span>
      <div
        className="ml-auto flex shrink-0 gap-1"
        role="presentation"
        onClick={(e) => e.stopPropagation()}
      >
        {isTruncated && (
          <Button
            className="-m-0.5 size-6"
            size="icon"
            variant="ghost"
            title={
              isExpanded
                ? t("personalization.memoryCandidate.actions.collapse")
                : t("personalization.memoryCandidate.actions.expand")
            }
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
        )}
        <Button
          className="hover:bg-primary/20 -m-0.5 size-6"
          disabled={approveMutation.isPending || dismissMutation.isPending}
          size="icon"
          title={t("personalization.memoryCandidate.actions.saveToMemory")}
          variant="ghost"
          onClick={() => approveMutation.mutate()}
        >
          <Check className="text-success h-3 w-3" />
        </Button>
        <Button
          className="hover:bg-destructive/20 -m-0.5 size-6"
          disabled={approveMutation.isPending || dismissMutation.isPending}
          size="icon"
          title={t("personalization.memoryCandidate.actions.dismiss")}
          variant="ghost"
          onClick={() => dismissMutation.mutate()}
        >
          <X className="text-destructive h-3 w-3" />
        </Button>
      </div>
    </Badge>
  )
}
